import React, { useRef } from 'react';
import moment from 'moment';
import { LoadDropdown, useFormContext, Option } from 'framework/form';
import useTimeTrackingService from './useTimeTrackingService';
import { get } from 'lodash';
import type { RedmineTimeTrackDto } from './types';

interface TimeEntryInputProps {
  source: string;
  issueIdSource: string;
  trackingDateSource: string;
  currentTimeTrackSource: string;
  correctTrackingDateSource?: string;
  correctTimeTrackSource?: string;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  disabled?: boolean;
}

const TimeEntryInput: React.FC<TimeEntryInputProps> = ({
  source,
  issueIdSource,
  trackingDateSource,
  currentTimeTrackSource,
  correctTrackingDateSource,
  correctTimeTrackSource,
  label = 'Time entry',
  placeholder = 'Select time entry',
  isRequired = false,
  disabled = false,
  ...props
}) => {
  const timeTrackingService = useTimeTrackingService();
  const { data = {}, onChangeCallback } = useFormContext();
  const timeTracksRef = useRef<Map<number, RedmineTimeTrackDto>>(new Map());

  const issueId = get(data, issueIdSource);
  const currentTimeEntryId = get(data, source);

  const loadDataMethod = async () => {
    if (!issueId) {
      return { result: [] };
    }

    const timeTracks = await timeTrackingService.getTimeTracksForIssue(Number(issueId));

    // Store time tracks in ref for later access
    timeTracksRef.current.clear();
    timeTracks.forEach((timeTrack: RedmineTimeTrackDto) => {
      timeTracksRef.current.set(timeTrack.id, timeTrack);
    });

    // Validate current time entry against loaded options.
    const numericTimeEntryId =
      currentTimeEntryId === undefined || currentTimeEntryId === null || currentTimeEntryId === ''
        ? null
        : Number(currentTimeEntryId);

    if (numericTimeEntryId !== null) {
      const isValidId = numericTimeEntryId > 0 && timeTracks.some(tt => tt.id === numericTimeEntryId);

      if (!isValidId) {
        const clearData: any = {
          [source]: null,
        };

        onChangeCallback?.(clearData);
      }
    }

    return { result: timeTracks };
  };

  const mapResults = (timeTrack: RedmineTimeTrackDto): Option => {
    const hoursText = timeTrack.hours ? ` (${timeTrack.hours}h)` : '';
    const commentsText = timeTrack.comments ? `: ${timeTrack.comments}` : '';

    return {
      value: timeTrack.id.toString(),
      label: `#${timeTrack.id}${hoursText}${commentsText}`,
    };
  };

  const handleChange = (values: any) => {
    const timeEntryId = values[source];

    if (timeEntryId && timeTracksRef.current.has(Number(timeEntryId))) {
      const selectedTimeTrack = timeTracksRef.current.get(Number(timeEntryId))!;

      // Format the date to ensure it's in YYYY-MM-DD format
      let formattedDate = '';
      if (selectedTimeTrack.spent_on) {
        // Parse the date and format it as YYYY-MM-DD
        const dateMoment = moment(selectedTimeTrack.spent_on);
        if (dateMoment.isValid()) {
          formattedDate = dateMoment.format('YYYY-MM-DD');
        } else {
          // Fallback: try to extract date part if it's in ISO format
          const date = selectedTimeTrack.spent_on.split('T')[0];
          formattedDate = date;
        }
      }

      // Update form with time entry data
      // Update all fields together
      const updateData: any = {
        [source]: timeEntryId,
        [trackingDateSource]: formattedDate,
        [currentTimeTrackSource]: selectedTimeTrack.hours?.toString() || '',
      };

      // Also prefill correct fields if they exist
      if (correctTrackingDateSource) {
        updateData[correctTrackingDateSource] = formattedDate;
      }
      if (correctTimeTrackSource) {
        updateData[correctTimeTrackSource] = selectedTimeTrack.hours?.toString() || '';
      }

      onChangeCallback?.(updateData);
    } else {
      // If cleared, just update the time entry field
      onChangeCallback?.(values);
    }
  };

  return (
    <LoadDropdown
      source={source}
      loadDataMethod={loadDataMethod}
      mapResults={mapResults}
      deps={[issueId]}
      isClearValueByUpdateDeps={false}
      disabled={disabled || !issueId}
      onChangeCallback={handleChange}
      // @ts-ignore - These props are passed through to Dropdown
      label={label}
      placeholder={placeholder}
      isRequired={isRequired}
      {...props}
    />
  );
};

export default TimeEntryInput;
